import { create } from "zustand";
import type { ChatConversation, ChatMessage } from "@ide/shared";
import { desktop, navigateTo } from "@/desktop/bridge";
import { useDesktopUi } from "@/desktop/ui";
import { get } from "@/lib/api";
import { playBell } from "@/lib/bell";
import { osNotify } from "@/lib/os-notify";
import { keys, queryClient } from "@/lib/queries";
import { activeWorkspace } from "@/workspace/file-ops";

/**
 * Avisos de mensagem nova: o sino, a notificação do sistema (Windows, Linux, macOS) e o balão no
 * canto inferior esquerdo com a foto de quem mandou e as primeiras palavras. Cada um pode ser
 * desligado nas configurações (ou no próprio balão).
 */
export interface AlertPrefs {
  sound: boolean;
  system: boolean;
  bubbles: boolean;
  /** Não perturbe: nenhum aviso de mensagem (nem som, nem notificação, nem balão). */
  dnd: boolean;
}

const KEY = "kompil.messageAlerts";

function read(): AlertPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<AlertPrefs> | null;
    return {
      sound: raw?.sound !== false,
      system: raw?.system !== false,
      bubbles: raw?.bubbles !== false,
      dnd: raw?.dnd === true,
    };
  } catch {
    return { sound: true, system: true, bubbles: true, dnd: false };
  }
}

export const useAlertPrefs = create<AlertPrefs & { set(patch: Partial<AlertPrefs>): void }>((set, get) => ({
  ...read(),
  set: (patch) => {
    set(patch);
    const { sound, system, bubbles, dnd } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify({ sound, system, bubbles, dnd }));
    } catch {
      // sem armazenamento: vale só nesta sessão
    }
  },
}));

/** Um balão por conversa: mensagens seguidas da mesma conversa atualizam o balão e somam. */
export interface ChatHead {
  conversationId: string;
  sender: NonNullable<ChatMessage["sender"]>;
  text: string;
  /** Nome do grupo ou do projeto (vazio na conversa particular). */
  where: string;
  count: number;
  at: number;
}

export const useChatHeads = create<{ heads: ChatHead[]; dismiss(conversationId: string): void }>((set) => ({
  heads: [],
  dismiss: (conversationId) =>
    set((s) => ({ heads: s.heads.filter((h) => h.conversationId !== conversationId) })),
}));

/** Até `max` palavras (e no máximo 90 letras), com reticências se cortou. */
export function firstWords(text: string, max = 12): string {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  let out = words.slice(0, max).join(" ");
  if (out.length > 90) out = out.slice(0, 90).replace(/\s+\S*$/, "");
  return out.length < text.replace(/\s+/g, " ").trim().length ? `${out}…` : out;
}

const conversationOf = (id: string) =>
  queryClient
    .getQueryData<{ conversations: ChatConversation[] }>(keys.conversations)
    ?.conversations.find((c) => c.id === id);

/** Abre a conversa: o chat do projeto aberto vira a aba Chat; as outras, a página Mensagens. */
export function openConversation(conversationId: string) {
  const conv = conversationOf(conversationId);
  const project = activeWorkspace.current?.detail.project.id;
  if (desktop) desktop.focus();
  else window.focus();
  if (conv?.kind === "project" && project && conv.projectId === project) {
    useDesktopUi.getState().set({ side: "team" });
  } else navigateTo(`/chat/${conversationId}`);
  useChatHeads.getState().dismiss(conversationId);
}

function bodyOf(message: ChatMessage) {
  if (message.kind === "image") return message.body ? `📷 ${message.body}` : "📷 Imagem";
  if (message.kind === "file")
    return `📎 ${message.file?.name ?? "Arquivo"}${message.body ? ` — ${message.body}` : ""}`;
  return message.body ?? "";
}

function systemNotification(message: ChatMessage, conv: ChatConversation | undefined) {
  const title =
    conv?.kind === "direct" || !conv
      ? (message.sender?.name ?? "Mensagem")
      : `${message.sender?.name ?? ""} · ${conv.title}`;
  // O som é sempre o do Kompil (o sino, que a pessoa pode desligar): a notificação do sistema é muda.
  osNotify({
    id: `chat-${message.conversationId}-${message.id}`,
    title,
    body: firstWords(bodyOf(message), 30),
    icon: message.sender?.avatarUrl ?? null,
    onClick: () => openConversation(message.conversationId),
  });
}

/**
 * Mensagem nova de outra pessoa numa conversa que não está à vista: sino, notificação do sistema e
 * balão (o que estiver ligado). Conversas silenciadas não avisam.
 */
export async function alertIncoming(message: ChatMessage, viewing: boolean) {
  if (!message.sender || message.kind === "system" || viewing) return;
  if (useAlertPrefs.getState().dnd) return;
  // Conversa ainda fora da lista (primeira mensagem, lista não carregada): busca antes de avisar,
  // para uma conversa silenciada nunca tocar.
  let conv = conversationOf(message.conversationId);
  if (!conv) {
    await queryClient
      .fetchQuery({
        queryKey: keys.conversations,
        queryFn: () => get<{ conversations: ChatConversation[]; unread: number }>("/chat/conversations"),
      })
      .catch(() => null);
    conv = conversationOf(message.conversationId);
  }
  if (conv?.muted) return;
  const prefs = useAlertPrefs.getState();
  if (prefs.dnd) return;
  if (prefs.sound) playBell();
  // Com a janela em segundo plano, a notificação do sistema; à vista, ela também (como no Teams).
  if (prefs.system) systemNotification(message, conv);
  if (prefs.bubbles) {
    const sender = message.sender;
    useChatHeads.setState((s) => {
      const prev = s.heads.find((h) => h.conversationId === message.conversationId);
      const head: ChatHead = {
        conversationId: message.conversationId,
        sender,
        text: firstWords(bodyOf(message)),
        where: conv && conv.kind !== "direct" ? conv.title : "",
        count: (prev?.count ?? 0) + 1,
        at: Date.now(),
      };
      // O mais novo embaixo, na frente; no máximo quatro conversas.
      return {
        heads: [...s.heads.filter((h) => h.conversationId !== message.conversationId), head].slice(-4),
      };
    });
  }
}
